import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { ChevronDown } from "lucide-react";
import { removeAccents } from "utils/utils";
import {
  validateCompanyRFC,
  validatePhoneNotAdvisor,
  validatePhoneNumber,
} from "utils/validators/generalValidators";
import { validateName } from "utils/validators/tenantFormValidators";

import { useCreateTenant } from "hooks/useCreateTenant";
import { useScrollIndicator } from "hooks/useScrollIndicator";
import { useUpdateTenant } from "hooks/useUpdateTenant";

import { Button } from "js/components/ui/button";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import { RadioGroup, RadioGroupItem } from "js/components/ui/radio-group";
import { Separator } from "js/components/ui/separator";

import { CountryCodeSelect } from "../../pages/Tenants/components/CountryCodeSelect";

interface ModernTenantModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (updatedData?: any) => void;
  selectedTenant?: any;
}

interface TenantFormData {
  name: string;
  first_surname: string;
  second_surname: string;
  email: string;
  phone_number: string;
  dial_code: string;
  company_rfc: string;
  legal_entity_name: string;
}

const initialFormData: TenantFormData = {
  name: "",
  first_surname: "",
  second_surname: "",
  email: "",
  phone_number: "",
  dial_code: "+52",
  company_rfc: "",
  legal_entity_name: "",
};

export const ModernTenantModal = ({
  isOpen,
  onOpenChange,
  onSuccess,
  selectedTenant,
}: ModernTenantModalProps) => {
  const isEditing = !!selectedTenant?.id;
  const [personType, setPersonType] = useState<"fisica" | "moral">("fisica");
  const [formData, setFormData] = useState<TenantFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEmailValid, setIsEmailValid] = useState(false);

  const emailInputRef = useRef<EmailInputRef>(null);
  const { userData } = useSelector((state: any) => state.entities.user);

  const createTenantMutation = useCreateTenant();
  const updateTenantMutation = useUpdateTenant();

  const isSubmitting =
    createTenantMutation.isPending || updateTenantMutation.isPending;

  const { scrollContainerRef, showScrollIndicator, scrollToBottom } =
    useScrollIndicator([isOpen, formData]);

  useEffect(() => {
    if (isOpen) {
      if (isEditing && selectedTenant) {
        setPersonType(
          selectedTenant.legal_type === "2" ||
            selectedTenant.legal_type === 2 ||
            selectedTenant.legal_type === "Legal"
            ? "moral"
            : "fisica",
        );
        setFormData({
          name: selectedTenant.name || "",
          first_surname: selectedTenant.first_surname || "",
          second_surname: selectedTenant.second_surname || "",
          email: selectedTenant.email || "",
          phone_number: selectedTenant.phone_number || "",
          dial_code: selectedTenant.dial_code || "+52",
          company_rfc: selectedTenant.company_rfc || selectedTenant.rfc || "",
          legal_entity_name: selectedTenant.legal_entity_name || "",
        });
      } else {
        setPersonType("fisica");
        setFormData(initialFormData);
        emailInputRef.current?.clear();
      }
      setErrors({});
      setIsEmailValid(false);
    }
  }, [isOpen, isEditing, selectedTenant]);

  const handleInputChange = (field: keyof TenantFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    return cleaned.slice(0, 10);
  };

  const validateField = async (
    field: string,
    value: any,
  ): Promise<string | null> => {
    try {
      switch (field) {
        case "name":
        case "first_surname":
        case "second_surname":
          if (field === "second_surname" && !value) return null;
          await validateName(null, value);
          break;
        // Email validation is handled by EmailInput component
        case "phone_number":
          await validatePhoneNumber(null, value);
          await validatePhoneNotAdvisor(userData, value);
          break;
        case "company_rfc":
          if (personType === "moral") {
            await validateCompanyRFC(null, value);
          }
          break;
        case "legal_entity_name":
          if (personType === "moral") {
            const alphanumeric120Regex = /^[a-zA-Z0-9&ñÑ\s]{1,120}$/;
            if (!value || !alphanumeric120Regex.test(value)) {
              return "Debe contener de 1 a 120 caractéres alfanuméricos";
            }
          }
          break;
      }
      return null;
    } catch (error: any) {
      return error.message;
    }
  };

  const handleBlur = async (field: keyof TenantFormData) => {
    const value = formData[field];
    const error = await validateField(field, value);
    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const hasBasicFormatErrors = (): boolean => {
    // Email validation is now handled by EmailInput component

    if (formData.phone_number && formData.phone_number.length > 0) {
      const phoneRegex = /^\d{10}$/;
      if (
        formData.phone_number.length === 10 &&
        !phoneRegex.test(formData.phone_number)
      ) {
        return true;
      }
      if (formData.phone_number.length < 10) {
        return true;
      }
    }

    if (
      personType === "moral" &&
      formData.company_rfc &&
      formData.company_rfc.trim() !== ""
    ) {
      const rfcRegex =
        /^([A-ZÑ\x26]{3}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{2}([A]|[0-9]){1})?$/;
      if (!rfcRegex.test(formData.company_rfc)) {
        return true;
      }
    }

    return false;
  };

  const isFormValid = (): boolean => {
    const baseFieldsValid =
      !!formData.name &&
      !!formData.first_surname &&
      !!formData.email &&
      !!formData.phone_number;

    if (personType === "moral") {
      const allFieldsPresent =
        baseFieldsValid &&
        !!formData.company_rfc &&
        !!formData.legal_entity_name;
      return (
        allFieldsPresent &&
        Object.keys(errors).length === 0 &&
        !hasBasicFormatErrors()
      );
    }

    return (
      baseFieldsValid &&
      Object.keys(errors).length === 0 &&
      !hasBasicFormatErrors()
    );
  };

  const handleSubmit = async () => {
    // Validate all fields before submitting
    const fieldsToValidate: (keyof TenantFormData)[] = [
      "name",
      "first_surname",
      "phone_number",
    ];

    if (formData.second_surname?.trim()) {
      fieldsToValidate.push("second_surname");
    }

    if (personType === "moral") {
      fieldsToValidate.push("company_rfc", "legal_entity_name");
    }

    // Run validation on all fields
    const validationErrors: Record<string, string> = {};
    for (const field of fieldsToValidate) {
      const error = await validateField(field, formData[field]);
      if (error) {
        validationErrors[field] = error;
      }
    }

    // Validate email using EmailInput ref
    const emailIsValid = await emailInputRef.current?.validate();
    if (!emailIsValid) {
      return;
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!isFormValid()) {
      return;
    }

    const sanitizeOptionalField = (value?: string) =>
      value && value.trim() ? removeAccents(value) : "";

    const payload: any = {
      name: removeAccents(formData.name),
      first_surname: removeAccents(formData.first_surname),
      phone_number: formData.phone_number,
      dial_code: formData.dial_code,
      email: formData.email,
      legal_type: personType === "fisica" ? "1" : "2",
    };

    if (formData.second_surname?.trim()) {
      payload.second_surname = sanitizeOptionalField(formData.second_surname);
    }

    if (personType === "moral") {
      payload.company_rfc = formData.company_rfc;
      payload.rfc = formData.company_rfc;
      payload.legal_entity_name = removeAccents(formData.legal_entity_name);
    }

    if (isEditing) {
      const sanitizedPayload = { ...payload };
      delete sanitizedPayload.legal_type;

      updateTenantMutation.mutate(
        { data: payload, id: selectedTenant.id },
        {
          onSuccess: (data) => {
            onOpenChange(false);
            if (onSuccess) onSuccess(sanitizedPayload);
          },
          onError: (error: any) => {
            console.error("Error updating tenant:", error);
          },
        },
      );
    } else {
      createTenantMutation.mutate(payload, {
        onSuccess: (data) => {
          onOpenChange(false);
          if (onSuccess) onSuccess(data);
        },
        onError: (error: any) => {
          console.error("Error creating tenant:", error);
        },
      });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full max-w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-none sm:rounded-lg"
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-4 shrink-0">
          <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900">
            {isEditing ? "Editar Inquilino" : "Crear Inquilino"}
          </DialogTitle>
        </DialogHeader>

        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto relative"
        >
          {showScrollIndicator && (
            <div className="fixed bottom-24 right-4 z-50">
              <button
                onClick={scrollToBottom}
                className="bg-gray-500/80 backdrop-blur text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-1 hover:bg-gray-600/80 transition-colors"
              >
                <span className="text-xs font-medium">Ver más</span>
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Tipo de persona
                </Label>
                {isEditing ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <div className="text-sm font-semibold text-gray-900">
                      {personType === "fisica"
                        ? "Persona Física"
                        : "Persona Moral"}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {personType === "fisica"
                        ? "Individuos con RFC e identificación"
                        : "Empresas o entidades legales"}
                    </div>
                  </div>
                ) : (
                  <RadioGroup
                    value={personType}
                    onValueChange={(value: "fisica" | "moral") => {
                      setPersonType(value);
                      setFormData(initialFormData);
                      setErrors({});
                      setIsEmailValid(false);
                      emailInputRef.current?.clear();
                    }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    <label
                      htmlFor="persona-fisica"
                      className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
                        personType === "fisica"
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-gray-300"
                      }`}
                    >
                      <RadioGroupItem
                        value="fisica"
                        id="persona-fisica"
                        className="mt-0.5"
                      />
                      <div className="ml-2.5">
                        <div className="text-sm font-medium text-gray-900">
                          Persona Física
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Individuos con RFC e identificación
                        </div>
                      </div>
                    </label>
                    <label
                      htmlFor="persona-moral"
                      className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
                        personType === "moral"
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-gray-300"
                      }`}
                    >
                      <RadioGroupItem
                        value="moral"
                        id="persona-moral"
                        className="mt-0.5"
                      />
                      <div className="ml-2.5">
                        <div className="text-sm font-medium text-gray-900">
                          Persona Moral
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Empresas o entidades legales
                        </div>
                      </div>
                    </label>
                  </RadioGroup>
                )}
              </div>

              <Separator className="my-6" />

              {personType === "moral" && (
                <h4 className="text-base font-semibold text-gray-900">
                  Datos del Representante Legal
                </h4>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label
                      htmlFor="name"
                      className="text-sm font-medium text-gray-700 mb-2 block"
                    >
                      Nombre *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value.toUpperCase())
                      }
                      onBlur={() => handleBlur("name")}
                      className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.name ? "border-red-500" : ""}`}
                      placeholder="Ingresa el nombre"
                    />
                    {errors.name && (
                      <span className="text-red-500 text-xs mt-1">
                        {errors.name}
                      </span>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="first_surname"
                      className="text-sm font-medium text-gray-700 mb-2 block"
                    >
                      Apellido Paterno *
                    </Label>
                    <Input
                      id="first_surname"
                      type="text"
                      value={formData.first_surname}
                      onChange={(e) =>
                        handleInputChange(
                          "first_surname",
                          e.target.value.toUpperCase(),
                        )
                      }
                      onBlur={() => handleBlur("first_surname")}
                      className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.first_surname ? "border-red-500" : ""}`}
                      placeholder="Ingresa el apellido paterno"
                    />
                    {errors.first_surname && (
                      <span className="text-red-500 text-xs mt-1">
                        {errors.first_surname}
                      </span>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="second_surname"
                      className="text-sm font-medium text-gray-700 mb-2 block"
                    >
                      Apellido Materno
                    </Label>
                    <Input
                      id="second_surname"
                      type="text"
                      value={formData.second_surname}
                      onChange={(e) =>
                        handleInputChange(
                          "second_surname",
                          e.target.value.toUpperCase(),
                        )
                      }
                      onBlur={() => handleBlur("second_surname")}
                      className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.second_surname ? "border-red-500" : ""}`}
                      placeholder="Ingresa el apellido materno"
                    />
                    {errors.second_surname && (
                      <span className="text-red-500 text-xs mt-1">
                        {errors.second_surname}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-x-2 md:gap-y-0">
                  <div className="md:row-span-2 min-w-0">
                    <EmailInput
                      ref={emailInputRef}
                      value={formData.email}
                      onChange={(value) => handleInputChange("email", value)}
                      onValidationChange={(valid) => setIsEmailValid(valid)}
                      label="Correo Electrónico"
                      placeholder="correo@ejemplo.com"
                      required
                      userData={userData}
                      showIcon
                      showStatus
                      className="h-11"
                    />
                  </div>

                  <div className="md:contents">
                    <Label
                      htmlFor="phone_number"
                      className="text-sm font-medium text-gray-700 mb-1.5 block md:col-span-2"
                    >
                      Teléfono *
                    </Label>
                    <div className="flex items-center gap-2 md:contents">
                      <div className="shrink-0">
                        <CountryCodeSelect
                          value={formData.dial_code}
                          onValueChange={(value) =>
                            handleInputChange("dial_code", value)
                          }
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <Input
                          id="phone_number"
                          type="tel"
                          value={formData.phone_number}
                          onChange={(e) =>
                            handleInputChange(
                              "phone_number",
                              formatPhoneNumber(e.target.value),
                            )
                          }
                          onBlur={() => handleBlur("phone_number")}
                          className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.phone_number ? "border-red-500" : ""}`}
                          placeholder="1234567890"
                          maxLength={10}
                        />
                        {errors.phone_number && (
                          <span className="text-red-500 text-xs mt-1 block">
                            {errors.phone_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {personType === "moral" && (
                <>
                  <Separator className="my-6" />
                  <h4 className="text-base font-semibold text-gray-900">
                    Datos de la Empresa
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="company_rfc"
                        className="text-sm font-medium text-gray-700 mb-2 block"
                      >
                        RFC de la Empresa *
                      </Label>
                      <Input
                        id="company_rfc"
                        type="text"
                        value={formData.company_rfc}
                        onChange={(e) =>
                          handleInputChange(
                            "company_rfc",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={() => handleBlur("company_rfc")}
                        className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.company_rfc ? "border-red-500" : ""}`}
                        placeholder="ABC123456789"
                        maxLength={13}
                      />
                      {errors.company_rfc && (
                        <span className="text-red-500 text-xs mt-1 block">
                          {errors.company_rfc}
                        </span>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="legal_entity_name"
                        className="text-sm font-medium text-gray-700 mb-2 block"
                      >
                        Razón Social *
                      </Label>
                      <Input
                        id="legal_entity_name"
                        type="text"
                        value={formData.legal_entity_name}
                        onChange={(e) =>
                          handleInputChange(
                            "legal_entity_name",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={() => handleBlur("legal_entity_name")}
                        className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${errors.legal_entity_name ? "border-red-500" : ""}`}
                        placeholder="Nombre de la empresa"
                        maxLength={120}
                      />
                      {errors.legal_entity_name && (
                        <span className="text-red-500 text-xs mt-1 block">
                          {errors.legal_entity_name}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-row items-center pt-4 border-t border-gray-200 shrink-0">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="h-11 px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancelar
          </Button>

          <div className="flex-1"></div>

          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || isSubmitting}
            className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>{isEditing ? "Guardando..." : "Creando..."}</>
            ) : (
              <>{isEditing ? "Guardar Cambios" : "Crear Inquilino"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
